import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Form, Row, Col, Badge } from 'react-bootstrap';
import { FiLayers, FiRefreshCw, FiEye, FiSearch, FiDownload } from 'react-icons/fi';
import { fetchTripsSubcoveragesRequest } from '../../store/actions/tripsActions';
import { useTranslation } from '../../hooks/useTranslation';
import ExportSubcoveragesTripModal from '../../components/Common/ExportSubcoveragesTripModal';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsSubcoverages.css';

const EMPTY_SUBCOVERAGES_FILTERS = {
    company_name: '',
    reference_name: '',
    category: '',
};

const AdminTripsSubcoverages = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const subcoveragesState = useSelector((state) => state.trips.subcoveragesTrip);

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        clearSearchState,
    } = usePersistedSearchState('trips-subcoverages', EMPTY_SUBCOVERAGES_FILTERS, {
        persistWhen: isAuthenticated,
    });
    const isApplyingFiltersRef = useRef(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportSuccess, setExportSuccess] = useState(null);

    // Charger les subcoverages avec pagination et filtres
    // Correspond à l'endpoint: GET /pagination/subcoverages-trip
    const loadSubcoverages = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        // Utiliser les filtres fournis ou les filtres appliqués
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        // Ajouter les filtres non vides aux paramètres de l'API
        if (activeFilters && typeof activeFilters === 'object') {
            Object.keys(activeFilters).forEach((key) => {
                const value = activeFilters[key];
                // Vérifier que la valeur existe et n'est pas vide
                if (value !== null && value !== undefined && value !== '') {
                    const trimmedValue = String(value).trim();
                    if (trimmedValue !== '') {
                        params[key] = trimmedValue;
                    }
                }
            });
        }
        
        // Log pour débogage
        console.log('🔍 loadSubcoverages - filtersToUse:', filtersToUse);
        console.log('🔍 loadSubcoverages - appliedFilters:', appliedFilters);
        console.log('🔍 loadSubcoverages - activeFilters:', activeFilters);
        console.log('🔍 loadSubcoverages - params finaux:', params);

        dispatch(fetchTripsSubcoveragesRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            loadSubcoverages(currentPage);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [isAuthenticated]);

    useEffect(() => {
        // Ne pas charger si on est en train d'appliquer des filtres (évite les conflits)
        if (!isApplyingFiltersRef.current) {
            loadSubcoverages(currentPage);
        }
    }, [currentPage, loadSubcoverages]);

    const subcoveragesData = useMemo(() => {
        const raw = subcoveragesState?.data;
        console.log('📊 subcoveragesData - raw data:', raw);
        if (!raw) {
            console.log('⚠️ subcoveragesData - Pas de données (raw est null/undefined)');
            return [];
        }
        const result = raw?.subcoverages_trip || raw?.data || raw || [];
        console.log('📊 subcoveragesData - résultat extrait:', result);
        console.log('📊 subcoveragesData - est un array?', Array.isArray(result));
        console.log('📊 subcoveragesData - longueur:', Array.isArray(result) ? result.length : 'N/A');
        return result;
    }, [subcoveragesState?.data]);

    const subcoveragesArray = Array.isArray(subcoveragesData) ? subcoveragesData : [];

    const meta = useMemo(() => {
        return subcoveragesState?.data?.meta || {
            total: 0,
            skip: 0,
            limit: itemsPerPage,
            page: currentPage,
            total_pages: 0,
        };
    }, [subcoveragesState?.data, itemsPerPage, currentPage]);

    const totalPages = meta.total_pages || Math.max(1, Math.ceil((meta.total || 0) / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + subcoveragesArray.length, totalItems);

    const handleRefresh = () => {
        loadSubcoverages(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        const newFilters = { ...filters };
        console.log('✅ handleApplyFilters - filters actuels:', filters);
        console.log('✅ handleApplyFilters - newFilters à appliquer:', newFilters);
        isApplyingFiltersRef.current = true;
        setAppliedFilters(newFilters);
        setCurrentPage(1);
        // Appeler directement avec les nouveaux filtres pour éviter les conflits avec useEffect
        loadSubcoverages(1, newFilters);
        // Réinitialiser le flag après un court délai
        setTimeout(() => {
            isApplyingFiltersRef.current = false;
        }, 200);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_SUBCOVERAGES_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadSubcoverages(1, emptyFilters);
        }, 100);
    };

    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    const getDeep = (obj, path, fallback = 'N/A') => {
        try {
            const val = path.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
            return val ?? fallback;
        } catch {
            return fallback;
        }
    };

    const handleDetails = (item) => {
        const subcoverageId = getDeep(item, ['subcoverage_trip', 'subcoverage_trip_id'], null);
        if (subcoverageId) {
            navigate(`/admin/trips/subcoverages/${subcoverageId}`);
        }
    };

    const { startPage, endPage } = getPaginationRange();

    return (
        <div className="dashboard-page admin-trips-subcoverages-page">
            <Card>
                <div>
                    <h2 className="page-title">
                        <FiLayers className="me-2" />
                        {t('pages.adminTripsSubcoverages.title')}
                    </h2>
                    <p className="text-muted mb-0">{t('pages.adminTripsSubcoverages.subtitle')}</p>
                </div>
                <Card.Body>
                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-success"
                            onClick={() => setShowExportModal(true)}
                            className="d-flex align-items-center"
                        >
                            <FiDownload className="me-2" />
                            {t('pages.adminTripsSubcoverages.export.button')}
                        </Button>
                        <Button variant="outline-secondary" onClick={handleRefresh} disabled={subcoveragesState.loading} className="d-flex align-items-center">
                            <FiRefreshCw className={`me-2 ${subcoveragesState.loading ? 'spinning' : ''}`} />
                            {t('common.refresh')}
                        </Button>
                    </div>

                    <ExportSubcoveragesTripModal
                        show={showExportModal}
                        onHide={() => setShowExportModal(false)}
                        onSuccess={(message) => setExportSuccess(message)}
                    />

                    {exportSuccess && (
                        <Alert variant="success" className="mt-3" onClose={() => setExportSuccess(null)} dismissible>
                            {exportSuccess}
                        </Alert>
                    )}

                    {subcoveragesState.error && (
                        <Alert variant="danger" className="mt-3">
                            {subcoveragesState.error}
                        </Alert>
                    )}

                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">{t('pages.adminTripsSubcoverages.filters.title')}</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    {t('common.reset')}
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    {t('common.applyFilters')}
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label className="mb-1">{t('pages.adminTripsSubcoverages.filters.searchName')}</Form.Label>
                                        <div className="search-box">
                                            <FiSearch className="search-icon" />
                                            <Form.Control
                                                type="text"
                                                name="reference_name"
                                                className="search-input"
                                                value={filters.reference_name}
                                                onChange={handleFilterChange}
                                                onKeyPress={handleKeyPress}
                                                placeholder={t('pages.adminTripsSubcoverages.filters.searchNamePlaceholder')}
                                            />
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label className="mb-1">{t('pages.adminTripsSubcoverages.filters.category')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="category"
                                            value={filters.category}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder={t('pages.adminTripsSubcoverages.filters.categoryPlaceholder')}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label className="mb-1">{t('pages.adminTripsSubcoverages.filters.companyName')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="company_name"
                                            value={filters.company_name}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder={t('pages.adminTripsSubcoverages.filters.companyNamePlaceholder')}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {subcoveragesState.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">{t('common.loading')}</p>
                        </div>
                    ) : subcoveragesState.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{subcoveragesState.error}</Alert>
                        </div>
                    ) : subcoveragesArray.length === 0 ? (
                        <div className="text-center py-5">
                            <FiLayers size={48} className="text-muted mb-3" />
                            <p className="text-muted">{t('pages.adminTripsSubcoverages.noData')}</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <div className="table-scroll">
                                    <Table hover striped className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>{t('pages.adminTripsSubcoverages.table.code')}</th>
                                                <th>{t('pages.adminTripsSubcoverages.table.type')}</th>
                                                <th>{t('pages.adminTripsSubcoverages.table.triggerHours')}</th>
                                                <th>{t('pages.adminTripsSubcoverages.table.city')}</th>
                                                <th>{t('pages.adminTripsSubcoverages.table.country')}</th>
                                                <th>{t('pages.adminTripsSubcoverages.table.reference')}</th>
                                                <th>{t('pages.adminTripsSubcoverages.table.partner')}</th>
                                                <th>{t('pages.adminTripsSubcoverages.table.effectivePeriod')}</th>
                                                <th>{t('pages.adminTripsSubcoverages.table.cashMaintain')}</th>
                                                <th>{t('common.actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {subcoveragesArray.map((item, index) => {
                                                const subcoverage = getDeep(item, ['subcoverage_trip'], {});
                                                const reference = getDeep(item, ['reference'], {});
                                                const partner = getDeep(item, ['partner_info'], {});

                                                return (
                                                    <tr key={getDeep(subcoverage, ['subcoverage_trip_id'], index)}>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{getDeep(subcoverage, ['subcoverage_trip_code'])}</td>
                                                        <td>
                                                            <Badge bg="info" text="dark">
                                                                {getDeep(subcoverage, ['trigger_type'])}
                                                            </Badge>
                                                        </td>
                                                        <td>{getDeep(subcoverage, ['trigger_number_hours'])}</td>
                                                        <td>{getDeep(subcoverage, ['city'])}</td>
                                                        <td>{getDeep(subcoverage, ['country'])}</td>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span>{getDeep(reference, ['name'])}</span>
                                                                <small className="text-muted">{getDeep(reference, ['reference_code'])}</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span>{getDeep(partner, ['company_name'])}</span>
                                                                <small className="text-muted">{`${getDeep(partner, ['first_name'], '')} ${getDeep(partner, ['last_name'], '')}`.trim() || 'N/A'}</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex flex-column">
                                                                <span>{getDeep(subcoverage, ['sub_coverage_start_date'])}</span>
                                                                <small className="text-muted">{getDeep(subcoverage, ['sub_coverage_end_date'])}</small>
                                                            </div>
                                                        </td>
                                                        <td>{getDeep(subcoverage, ['cash_share_maintain'])}</td>
                                                        <td>
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                onClick={() => handleDetails(item)}
                                                                className="d-flex align-items-center"
                                                            >
                                                                <FiEye className="me-1" />
                                                                {t('common.details')}
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted">
                                        {t('pages.adminTripsSubcoverages.pagination.showing', { from: indexOfFirstItem + 1, to: indexOfLastItem, total: totalItems })}
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.First
                                            onClick={() => setCurrentPage(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev
                                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                            disabled={currentPage === 1}
                                        />
                                        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((page) => (
                                            <Pagination.Item
                                                key={page}
                                                active={page === currentPage}
                                                onClick={() => setCurrentPage(page)}
                                            >
                                                {page}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Next
                                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last
                                            onClick={() => setCurrentPage(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsSubcoverages;

